import { useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router'
import { FormattedMessage } from 'react-intl'
import Popover from '@components/Popover/Popover'
import useAuth from '@hooks/useAuth'
import Loader from '@components/Loader/Loader'
import { getPlanFromPath } from 'src/constants/plans'

import loginVideoWebm from '@static/videos/login.webm'
import loginVideoPoster from '@static/images/login/login-bg.webp'
import logo from '@static/vectors/logo-datalab.svg'
import './Login.css'

type LoginState = 'idle' | 'loading' | 'success' | 'error' | 'not-found'

export default function Login() {
  const auth = useAuth()
  const location = useLocation()
  const [state, setState] = useState<LoginState>('idle')
  const [email, setEmail] = useState('')

  let from = (location.state as { from?: string } | null)?.from
  if (from?.startsWith('/')) from = from.substring(1)

  const requestedPlan = getPlanFromPath(from)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const value = formData.get('email')

    if (typeof value === 'string' && value.length > 0) {
      const normalized = value.toLocaleLowerCase()
      setEmail(normalized)
      try {
        setState('loading')
        await auth.requestEmail(normalized, from)
        setState('success')
      } catch (error) {
        console.warn('Login error:', error)
        const status = (error as { status?: number })?.status
        setState(status === 404 ? 'not-found' : 'error')
      }
    }
  }

  const showForm =
    state === 'idle' || state === 'error' || state === 'not-found'

  return (
    <div className="page" id="login">
      <div className="split">
        <div className="item item-left">
          <video autoPlay muted loop playsInline poster={loginVideoPoster}>
            <source src={loginVideoWebm} type="video/webm" />
            <img
              src={loginVideoPoster}
              alt="login animation fallback"
              aria-hidden="true"
            />
          </video>
        </div>
        <div className="item item-right">
          <form id="login-card" onSubmit={handleSubmit}>
            <img src={logo} alt="datalab logo" />
            <h1>
              <FormattedMessage id="login.title" />
            </h1>
            <p>
              <span>
                <FormattedMessage id="login.text" />
              </span>
              <Popover
                position="top"
                buttonClassName="login-info-button"
                triggerContent={
                  <span
                    id="info-popover"
                    className="icon icon-info"
                    aria-label="More information"
                  />
                }
              >
                <FormattedMessage
                  id="login.info.content"
                  defaultMessage="Info..."
                />
              </Popover>
            </p>
            {state === 'loading' && (
              <div className="login-loading">
                <Loader />
              </div>
            )}
            {state === 'success' && (
              <div className="login--mail-sent">
                <FormattedMessage id="login.email.text" />
              </div>
            )}

            {showForm && (
              <>
                {requestedPlan && (
                  <p className="login-plan-context">
                    <FormattedMessage
                      id="login.plan-context"
                      values={{
                        plan: (
                          <strong key="plan">
                            <FormattedMessage
                              id={`pricing.plans.${requestedPlan}.name`}
                            />
                          </strong>
                        )
                      }}
                    />
                  </p>
                )}

                <label>
                  <strong>
                    <FormattedMessage id="login.label" />
                  </strong>
                  <input
                    required
                    type="email"
                    name="email"
                    placeholder="email@example.com"
                    defaultValue={email}
                  />
                </label>

                {state === 'error' && (
                  <div className="error-message" role="alert">
                    <FormattedMessage id="login.error.generic" />
                  </div>
                )}

                {state === 'not-found' && (
                  <div className="error-message" role="alert">
                    <FormattedMessage id="login.error.not-found" />{' '}
                    <Link
                      to="/signup"
                      state={{
                        ...((location.state as object | null) ?? {}),
                        email
                      }}
                    >
                      <FormattedMessage id="login.error.not-found-link" />
                    </Link>
                  </div>
                )}

                <button className="cta-button">
                  <span className="icon icon-stars" />
                  <span>
                    <FormattedMessage id="login.cta-button" />
                  </span>
                </button>
              </>
            )}
            <p className="text-signup">
              <FormattedMessage
                id="login.text-signup"
                values={{
                  link: (
                    <Link
                      key="login-signup"
                      to="/signup"
                      state={location.state}
                    >
                      <FormattedMessage id="login.signup" />
                    </Link>
                  )
                }}
              />
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
